const Player = require('./models/Player');
const Deck = require('./models/Deck');
const Card = require('./models/Card');
const Bid = require('./models/Bid');
const Trick = require('./models/Trick');
const Auction = require('./Auction');
const DealParser = require('./DealParser');
const GameState = require('./GameState');
const ScoreBoard = require('./ScoreBoard');
const DDS = require('./lib/dds');
const {
	isSamePropValue,
	getLowestRank,
	getLowestPlayCard,
	getPlaysBySuits,
	intersection,
	getMaxScorePlays,
} = require('./utils');

const PLAYORDER = {
	N: ['N', 'E', 'S', 'W'],
	E: ['E', 'S', 'W', 'N'],
	S: ['S', 'W', 'N', 'E'],
	W: ['W', 'N', 'E', 'S'],
};

const NEXT_PLAYER_ORDER = {
	N: PLAYORDER['E'],
	E: PLAYORDER['S'],
	S: PLAYORDER['W'],
	W: PLAYORDER['N'],
};

const LEFT_OF_PLAYER = {
	N: 'E',
	E: 'S',
	S: 'W',
	W: 'N',
};

const VULNERABLE = ['NS', 'EW', 'All', 'None'];
const VULNERABLE_LOOKUP = { Love: 'None', '-': 'None', Both: 'All' };
// “None” , “Love” or ”-“	no vulnerability
// “All” or “Both”	both sides vulnerable
/**
	Class representing a Bridge game


	- A game has four players, in the NSEW seats.
	- Each player is dealt 13 Cards from the Deck
	- A Seat is assigned as the dealer seat.
	- The Dealer is the first to speak and can make a bid bidder
	- However the bidding is out of scope, and a contract is simply set, and a user is designated as the declarer.
	- The partner of the declarer is designated Dummy
	- The player to the left of the dealer is the first to play a card
	- The player who wins the trick, is designated as next to play a card, until all cards are played.

	@example
		let game = new Game("Example Game");
		game.newGame(); // creates 4 players and a deck of cards, sets South as dealer
		game.deck.shuffle(); // shuffle the deck

		game.setDealer(game.playerN); // sets North as dealer

		game.canPlay(); // false
		game.dealCards(); // deal cards equally to all players in orde
		game.canPlay(); // false

		// Dealer bids first,
		game.bid('1NT');
		game.bid('P');
		game.bid('P');
		game.bid('P');
		// Automatically sets the contract, declare and the trump suit based on the winning bid


		game.canPlay(); // true
		game.declarer; // game.PlayerN
		game.dummy; // game.PlayerS
		game.currentPlayer; // game.playerE - sits to the left of the declarer

	@example
		let game = new Game("Example Game");
		game.newGame(); // creates 4 players and a deck of cards, sets South as dealer

		game.bid('1NT');
		game.bid('P');
		game.bid('P');
		game.bid('P');

		game.playCard(game.deck.card2C); // plays a specific card
		game.playCard(game.currentPlayer.hand[0]); // plays the first card in the current players hand
		game.playCard(game.playerN.hand[0]); // plays the first card in the North players hand

		@param {string} name Name or identifier for the game. `e.g. Game 1`
		@param {string} board Board number of the game. `e.g. Game 1`
		@param {boolean} useDDS whether to calculate DDS scores after each card play default `true`
*/

class Game {
	constructor(name = undefined, board = undefined, useDDS = false) {
		this._players = [];
		this._name = name;
		this._board = board;
		this._useDDS = useDDS;

		/**
		 * The current contract for the game
		 * @type {String} a simple string for the contract - e.g. "4NT"
		 */
		this.contract = null;
		/**
		 * A list of tricks
		 * @type {Trick[]}
		 */
		this.tricks = [];

		/**
		 * The deck of cards used for this game.
		 * @type {Deck}
		 */
		this.deck = new Deck();

		/**
		 * The current turn in the game, starts at 0, and increments after every call to playCard
		 * @type {Number}
		 */
		this.turn = 0;

		this._auction = null;

		/**
		 * An internal reference to the current play order - e.g. `['N', 'E', 'S', 'W']`
		 *
		 * This uses a modulus(4) on `this.turn` to determine the next player.
		 *
		 * `this.seatOrder[this.turn % 4] = next player`
		 *
		 * @type {String[]}
		 * @private
		 */
		this.seatOrder = null;

		this._declarer = null;

		this._actions = [];

		this._vulnerable = 'None';

		this.claimedTricks = null;
	}

	/**
	 *  The arbitrary name for this game
	 *  @return {string} Name or identifier of this game
	 */
	get name() {
		return this._name;
	}

	/**
	 *  The board number for this game
	 *  @return {string} Board number of this game
	 */
	get board() {
		return this._board;
	}

	/**
	 * DDS methods
	 */

	/**
	 * Activate DDS score cacluation after each card is played
	 */
	enableDDS(doNextPlays = false) {
		this._useDDS = true;
		if (doNextPlays) {
			this.nextPlays();
		}
	}

	/**
	 * prevents DDS scores being calculated after each card is played
	 */
	disableDDS() {
		this._useDDS = false;
	}

	/**
	 * Gets the current setting of whether DDS scores are calculated
	 */
	get useDDS() {
		return this._useDDS;
	}

	/**
	 * Set the DDS score calculation config.
	 * @param  {Boolean} value
	 */
	set useDDS(value) {
		this._useDDS = value;
	}

	/**
	 * Sets up a new game with Players and a Deck of cards
	 */
	newGame() {
		this.createPlayers();
		this.createDeck();
		this.setDealer(this.playerS);
		this._auction = new Auction(this.playerS);
	}

	/**
	 *  Creates four players in the S, W, N, E seats
	 * @private
	 */
	createPlayers() {
		for (let POS of ['S', 'W', 'N', 'E']) {
			this._players.push(Player.fromString(POS));
		}
	}

	/**
	 * @return {Player[]}
	 */
	get players() {
		return this._players;
	}

	/**
	 * Gets the player in the given seat
	 * @param  {String} seatName seat representation e.g `N`
	 * @return {Player} the player for the seat
	 */
	playerBySeat(seatName) {
		let match = this.players.find((player) => player.name == seatName);
		return match;
	}

	/**
	 * Get the player in the North seat
	 * @return {Player} the North player
	 */
	get playerN() {
		return this.playerBySeat('N');
	}

	/**
	 * Get the player in the East seat
	 * @return {Player} the East player
	 */
	get playerE() {
		return this.playerBySeat('E');
	}

	/**
	 * Get the player in the South seat
	 * @return {Player} the South player
	 */
	get playerS() {
		return this.playerBySeat('S');
	}

	/**
	 * Get the player in the West seat
	 * @return {Player} the West player
	 */
	get playerW() {
		return this.playerBySeat('W');
	}

	/**
	 * Creates a deck of cards for the game
	 * @private
	 */
	createDeck() {
		this.deck = Deck.generate();
	}

	/**
	 * @param  {Player} player sets given player as dealer
	 *
	 */
	setDealer(player) {
		this._dealer = player;

		this._auction = new Auction(this.dealer);
		// player order is player to Dealers left, to take the first turn
		this.setPlayOrderForDealer();
	}

	/**
	 * Get the dealer set for this game
	 * @return {Player} the player that is set as dealer
	 */
	get dealer() {
		return this._dealer;
	}

	/**
	 * The instance of the Auction for this game
	 * @return {Auction} auction for this game
	 */
	get auctionInstance() {
		return this._auction;
	}

	/**
	 * A list of bids forming the auction for this game. The highest bid results in the contract being set, and the declarer being set.
	 * Values can be on of, `P`, `{1-7}[C,D,H,S, NT]`, `X`, `XX` - e.g, ["P", "1NT", "2C", "3H", "4NT"]`
	 *
	 * @type {String[]}
	 */
	get auction() {
		return this._auction != null ? this._auction.bidsAsList() : [];
	}

	get bids() {
		return this.auction;
	}

	/**
	 * The vulnerable state of this game, default `None`
	 * @return {String} the vulnerable state, one of `EW`, `NS`, `All`` or `None`
	 */
	get vulnerable() {
		return this._vulnerable;
	}

	/**
	 * Set the vulnerable state
	 * @param  {String} value - the vulnerable state, one of `EW`, `NS`, `All`` or `None`
	 */
	setVulnerable(value) {
		// The Vulnerable tag value defines the situation of vulnerability. The following tag values are possible:

		// “None” , “Love” or ”-“	no vulnerability
		// “NS”	North-South vulnerable
		// “EW”	East-West vulnerable
		// “All” or “Both”	both sides vulnerable
		// In export format the tag values “None” and “All” are applied.
		//
		value = VULNERABLE_LOOKUP[value] || value;

		if (!VULNERABLE.includes(value)) {
			throw new Error('Invalid value for vulnerable', value);
		}
		this._vulnerable = value;
	}

	/**
	 * @param {String} contract - Set the contract for the game, e.g. `"2NT"`, or `"2H"`
	 */
	setContract(contract) {
		// TODO: validate is {1-7}[C|D|H|S|T]
		this.contract = contract; // 3NT, 2H
	}

	/**
	 * Returns the trump suit for the current contract
	 * @return {String} one of C, D, H, S or NT, or `null` `
	 */
	contractSuit() {
		return this.contract ? this.contract.slice(1) : null;
	}

	/**
	 * Returns the amount of tricks set in contract
	 * @return {Number|null}
	 */
	get contractTricks() {
		return this.contract ? Number(this.contract[0]) : null;
	}

	/**
	 * Deals the pack of cards, a card to each player in turn,
	 * starting with the player left of the dealer
	 */
	dealCards() {
		this.emptyHands();

		let next = this.players.indexOf(this._dealer) + 1;
		// let nextPos = pos % 4;
		for (let card of this.deck.cards) {
			// deal to next player
			// this.players[next % 4].addCard(card);
			this.dealCard(card, this.players[next % 4]);
			next += 1;
		}
	}

	/**
	 * convert the dealt cards to a PBN deal string...
	 * starting with the PLAYER TO THE LEFT OF THE DECLARER
	 * @return {String} the PBN compatible Deal representation - eg: N:AT62.J73.Q84.K95 J73.Q84.K95.AT62 Q84.K95.AT62.J73 K95.AT62.J73.Q84
	 */
	get DDSDealString() {
		let dealString = this.dealString;

		// convert the dealt cards to a PBN deal string...
		// starting with the PLAYER TO THE LEFT OF THE DECLARER

		// For the purpose of this lib, we have to change the PBN string to start with the "first to play" rather than the dealer
		// the dankv/dds.js does not account for the contract.
		// see here https://github.com/danvk/dds.js/blob/cb9bf826838a087d1d1a0c70dd295dbec5093800/hand-explorer.js#L22
		// SO, it's set to the player to the left of the declarer _not_ the dealer, however, if there is no declarer, use dealer
		const first = this.declarer
			? LEFT_OF_PLAYER[this.declarer.name]
			: this.dealer.name;
		const order = PLAYORDER[first];

		let hands = [];
		for (let seat of order) {
			let player = this.playerBySeat(seat);
			// sort into S, H, D, C
			hands.push(player.handPBN);
		}
		let dealStr = `${this.currentPlayer.name}:${hands.join(' ')}`;
		return dealStr;
	}

	/**
	 * convert the dealt cards to a PBN deal string...
	 * starting with the Dealer
	 * @return {String} the PBN compatible Deal representation - eg: N:AT62.J73.Q84.K95 J73.Q84.K95.AT62 Q84.K95.AT62.J73 K95.AT62.J73.Q84
	 */
	get dealString() {
		// convert the dealt cards to a PBN deal string...
		// starting with dealer, get the list of cards
		let order = PLAYORDER[this.dealer.name];

		let hands = [];
		for (let seat of order) {
			let player = this.playerBySeat(seat);
			// sort into S, H, D, C
			hands.push(player.handPBN);
		}
		let dealStr = `${this.dealer.name}:${hands.join(' ')}`;
		return dealStr;
	}

	get hasCards() {
		return this.deck.hasCards;
	}

	get dealtCards() {
		return this.deck.cards.filter((d) => d.state != 'undealt');
	}
	get playableCards() {
		return this.deck.cards.filter(
			(d) => d.state != 'undealt' && d.state != 'played'
		);
	}

	/**
	 * Deal a card to a player
	 * @param  {Card} card the card instance to deal
	 * @param  {Player} player the player to which the card should be dealt
	 */
	dealCard(card, player) {
		player.addCard(card);
		card.player = player;
		card.state = 'dealt';
	}

	/**
	 * Arranges the hands of each player (see `Player.arrangeHand`)
	 */
	sortHands() {
		for (let player of this.players) {
			player.arrangeHand();
		}
	}

	/**
	 * Resets players hands of cards to empty
	 * @private
	 */
	emptyHands() {
		for (let player of this.players) {
			player.emptyHand();
		}
		// reset the cards
		this.deck.cards.map((card) => (card.state = 'undealt'));
	}

	/**
	 * Simple validation of bid syntax
	 * @param  {string} bid string to validate
	 * @return {boolean}  true if bid is valid
	 */
	validBid(bid) {
		//TODO validate;
		// one of P, X, XX
		// in form 1-7 + CDHS NT
		//
		//

		if (['P', 'X', 'XX'].includes(bid)) {
			return true;
		}

		if (['1', '2', '3', '4', '5', '6', '7'].includes(bid[0])) {
			if (['C', 'H', 'D', 'S', 'NT'].includes(bid.slice(1))) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Takes a list of bid strings, e.g.   `["P", "1NT", "2C", "3H", "4NT"`]`
	 * Each value can be on of, `P`, `{1-7}[C,D,H,S, NT]`, `X`, `XX`
	 * @param {[type]} bids -
	 */
	addBids(bids) {
		let _bids = [];
		for (let bid of bids) {
			let _bid = this.addBid(bid);

			_bids.push(_bid);
		}
		return _bids;
	}

	/**
	 * Add a bid to the auction
	 * Values can be on of, `P`, `{1-7}[C,D,H,S, NT]`, `X`, `XX`
	 *
	 * When the final bid is made and the auction is complete, the contract and declarer are also set
	 * @param {String} bid -e.g, one of `"P", "1NT", "2C", "3H", "4NT"`
	 */
	addBid(bidStr) {
		// rewrite N to NT
		//

		if (!this.validBid(bidStr)) {
			throw new Error(`Invalid bid ${bidStr}`);
		}
		let bid = this._auction.bid(
			bidStr,
			this.playerBySeat(this._auction.currentBidder)
		);

		this.addAction(bid);

		if (this._auction.isComplete) {
			this.setContract(this._auction.contract);
			this.setDeclarer(this.playerBySeat(this._auction.declarer));
			this.nextPlays();
		}

		return bid;
	}

	/**
	 * Sets the auction to an empty list
	 */
	clearBids() {
		this._auction = new Auction(this.dealer);
	}

	/**
	 * Sets the player who is declarar
	 * <pre><code>
	 * let game = new Game();
	 * game.newGame();
	 *
	 * game.setDeclarer(game.playerN); // set N as declarer
	 * </code></pre>
	 * @param {Player} declarer - the player who was first the play the highest suit in the auction
	 */
	setDeclarer(declarer) {
		this._declarer = declarer;
		this.setPlayOrderForDeclarer();
	}

	/**
	 * Get the declarer, who is the player who first bid the suit or NT that was highest in the auction.
	 * @type {Player} the player who is set as declarer
	 */
	get declarer() {
		return this._declarer;
	}

	get declaringPair() {
		return this._declarer.pair;
	}

	/**
	 * Gets the dummy player, who sits opposite the delarer
	 * @return {Player} the player desigated as dummy
	 */
	get dummy() {
		return this._declarer ? this.playerBySeat(this._declarer.partner) : null;
	}

	get declarerTricks() {
		return this.tricks.filter(
			({ winningPair }) => winningPair === this.declaringPair
		);
	}

	/**
	 * Number of tricks made by a declarer, including claimed tricks.
	 * @return {number}
	 */
	get madeTricks() {
		return (
			this.declarerTricks.length +
			(this.claimedTricks !== null ? this.claimedTricks : 0)
		);
	}

	/**
	 * Gets the winning pair of the game
	 * @return {"NS"|"EW"|null} the winning pair
	 */
	get winningPair() {
		if (!this.isComplete) return null;

		if (this.madeTricks >= this.minTricks) {
			return this.declaringPair;
		} else {
			return this.declarer.oppositePair;
		}
	}

	/**
	 * @return {boolean} true if cards are dealt, and contract is set and cards are left to be played
	 */
	canPlay() {
		return (
			this.players.length == 4 &&
			this.deck.cards.filter((card) => card.state == 'undealt').length == 0 &&
			this.contract != null
		);
	}

	/**
	 * @param {number} tricks - number of tricks to claim
	 * @return {boolean} true if player can claim a given number of tricks
	 */
	canClaim(tricks) {
		if (!this.canPlay()) {
			throw new Error('Not ready to play');
		}

		const cards = this.cardsPlayed.map((card) => card.name);

		const contractSuit = this.contractSuit();
		const trump = contractSuit === 'NT' ? 'N' : contractSuit;

		const result = DDS.nextPlays(this.DDSDealString, trump, cards, tricks);

		if (!result || result.error) {
			throw new Error(`(${result.error}) ${result.message}`);
		}

		// Find the maximum score
		const maxScore = result.plays.reduce((max, current) => {
			return Math.max(max, current.score);
		}, -Infinity);

		if (tricks <= maxScore) {
			this.claimedTricks = tricks;
			return true;
		}

		return false;
	}

	/**
	 * Determines if the player can play the given card to the given trick based on his hand
	 *
	 * A player can only play a card
	 * - If the card matches the trick suit
	 * - the player does not have any cards in the trick suit
	 *
	 * @param  {Player} player [description]
	 * @param  {Card} card   [description]
	 * @param  {Trick} trick  [description]
	 * @return {boolean}
	 */
	canPlayCardToTrick(card, trick) {
		if (trick.trickSuit && card.suitName == trick.trickSuit.short) {
			return true;
		}

		let suitCardsInHand = card.player.hand.filter(
			(card) => card.state == 'dealt' && card.suit == trick.trickSuit
		); // list of cards unplayed

		return suitCardsInHand.length == 0; // cannot play
	}

	/**
	 * Add a new trick to the list, with the given trumpSuit
	 * @param  {String} trumpSuit the suit that is trumps for this trick - one of C, H, D, S or NT
	 * @return {Trick} returns the created Trick instance
	 */
	nextTrick(trumpSuit = this.contractSuit()) {
		if (this.isComplete) {
			// game is over
			throw new Error('Game is complete');
		}

		// the next player is the winner of the current trick, or the player to the left of dealer
		if (this.currentTrick && this.currentTrick.winningCard) {
			this.setPlayOrderForWinner(this.currentTrick.winningCard.player);
		}

		// TODO: check the last trick has been played
		let nextTrick = new Trick(trumpSuit);
		this.tricks.push(nextTrick);

		return nextTrick;
	}

	/**
	 * Get a list of played tricks
	 * @return {Trick[]} the list of tricks that are complete
	 */
	get playedTricks() {
		return this.tricks.filter((trick) => trick.isComplete);
	}

	/**
	 * Set the play order starting with the player given.
	 * @param {[type]} winner - the player who won the last trick, starts the play
	 */
	setPlayOrderForWinner(winner) {
		this.seatOrder = PLAYORDER[winner.name];
	}

	/**
	 * Sets the play order, starting with the player to the left of dealer
	 */
	setPlayOrderForDealer() {
		this.seatOrder = NEXT_PLAYER_ORDER[this._dealer.name];
	}

	/**
	 * Sets the play order, starting with the player to the left of declarer.
	 * RULE: The first player to play a card, is to the left of the declarer
	 */
	setPlayOrderForDeclarer() {
		this.seatOrder = NEXT_PLAYER_ORDER[this._declarer.name];
	}

	/**
	 * Game is complete if 13 tricks have been played OR player successfully claimed some amount of tricks
	 * @return {boolean} true if the game is over
	 */
	get isComplete() {
		return (
			this.claimedTricks !== null ||
			(this.tricks.length === 13 && this.currentTrick.isComplete)
		);
	}

	/**
	 * The most recent trick in the list
	 * @return {Trick} the current, or last played trick
	 */
	get currentTrick() {
		return this.tricks[this.tricks.length - 1] || null;
	}

	/**
	 * Iterate all tricks and accumulate the score for each pair,
	 * @return {Object} - an object with the current score: `{ NS: 0, ES: 0}`
	 */
	get score() {
		// interate all tricks, and accumulate score for each pair

		let scores = { NS: 0, EW: 0 };

		this.tricks.forEach((trick) => {
			if (trick.winningPair) scores[trick.winningPair] += 1;
		});

		return scores;
	}

	get minTricks() {
		if (!this.contract) {
			return null;
		}
		return 6 + Number(this.contract[0]); // eg. 1NT = 7, 2NT = 8, 3NT = 9
	}

	/**
	 * Plays a card to the current trick
	 * @param  {Card} card the card to play in the trick
	 */
	playCard(card) {
		if (card.player.name != this.currentPlayer.name) {
			throw new Error('It is not this players turn');
		}
		if (!this.canPlay()) {
			throw new Error('Not ready to play');
		}

		if (!this.currentTrick) {
			this.nextTrick(this.contractSuit());
		} else if (this.currentTrick.isComplete) {
			this.nextTrick(this.contractSuit());
		}

		// TODO: check canPlayCardToTrick(card)
		//
		if (!this.canPlayCardToTrick(card, this.currentTrick)) {
			throw new Error('Cannot play card to trick');
		}
		this.currentTrick.addCard(card);

		this.addAction(card);

		this.updatePlayOrder();

		try {
			this.nextPlays();
		} catch (err) {
			console.error(err);
		}

		return card;
	}

	/**
	 * Adds an action to the game history
	 * @param {Card|Bid} action card play action
	 * @private
	 */
	addAction(action) {
		this._actions.push(action);
	}

	/**
	 * Returns the list of actions so far
	 * @return {Card[]|Bid[]} the list of actions of type Card or Bid
	 */
	get actions() {
		return this._actions;
	}

	/**
	 * A list of Card actions in order of play
	 * @return {Card[]} list of Cards played
	 */
	get cardsPlayed() {
		return this._actions.reduce((cards, action) => {
			if (action instanceof Card) {
				cards.push(action);
			}
			return cards;
		}, []);
		// return this._actions.map((action) => typeOfaction);
	}

	/**
	 * A list of Bid actions in order of play
	 * @return {Bid[]} list of Cards played
	 */
	get bidsMade() {
		return this._actions.reduce((bids, action) => {
			if (action instanceof Bid) {
				bids.push(action);
			}
			return bids;
		}, []);
	}

	/**
	 * @private
	 */
	updatePlayOrder() {
		this.turn += 1; // setup the next turn

		// the next player is the winner of the current trick, or the player to the left of dealer
		// if (this.currentTrick && this.currentTrick.winningCard) {
		// }

		// // if trick is complete, set the winner as the current player
		if (this.currentTrick.isComplete && !this.isComplete) {
			this.setPlayOrderForWinner(this.currentTrick.winningCard.player);
			// this.nextTrick(this.contractSuit());
		}
	}

	/**
	 * The player whos turn it currently is, changes after `playCard`.
	 * @return {Player} the current player
	 */
	get currentPlayer() {
		// since there are always 4 players, modulo 4 will return the index  position
		const turnIndex = this.turn % 4; // e.g. 1 % 4 == 1.
		const nextSeatToPlay = this.seatOrder[turnIndex]; // Seat order e.g. ["E", "S", "W", "N"]
		return this.playerBySeat(nextSeatToPlay); // get the player for this seat
	}

	/**
	 * Returns an object of game data in it's current state. this object can be serialized into JSON
	 *
	 * @example
	 *
	 * JSON.stringify(game.currentGameData); //
	 * 	{
	 *      "name": "Game Data Example",
	 *      "deck": [
	 *        "2C",
	 *        "3C",
	 *        "4C",
	 *				...
	 *      ],
	 *      "dealString": "S:26TA.37J.48Q.59K 37J.48Q.59K.26TA 48Q.59K.26TA.37J 59K.26TA.37J.48Q",
	 *      "dealer": "S",
	 *      "auction": {
	 *        "bids": [
	 *
	 *					{
	 *						"bid": "3S"
	 *						"type" : "contract"
	 *						"value" : "3"
	 *						"suit" : "S"
	 *						"player" : "S"
	 *					},
	 *					{
	 *						"bid": "3H"
	 *						"type" : "contract"
	 *						"value" : "3"
	 *						"suit" : "H"
	 *						"player" : "W"
	 *					},
	 *					{
	 *						"bid": "P"
	 *						"type" : "pass"
	 *						"value" : null,
	 *						"suit" : null,
	 *						"player" : "N"
	 *					},
	 *					{
	 *						"bid": "P"
	 *						"type" : "pass"
	 *						"value" : null
	 *						"suit" : null
	 *						"player" : "E"
	 *					}
	 *        ],
	 *        "currentBidder": "S",
	 *        "isComplete": true,
	 *        "declarer": "S"
	 *      },
	 *      "declarer": "S",
	 *      "dummy": "N",
	 *      "contract": "3H",
	 *      "actions": [
	 *        "2C",
	 *        "3C",
	 *        "4C",
	 *        "5C"
	 *      ],
	 *      "players": [
	 *        "S",
	 *        "W",
	 *        "N",
	 *        "E"
	 *      ],
	 *      "playerN": {
	 *        "hand": [
	 *          {
	 *            "name": "3C",
	 *            "state": "played"
	 *          },
	 *          {
	 *            "name": "7C",
	 *            "state": "dealt"
	 *          },
	 *          ...
	 *        ],
	 *        "pair": "NS"
	 *      },
	 *      "playerS": {
	 *        "hand": [
	 *					...
	 *        ],
	 *        "pair": "NS"
	 *      },
	 *      "playerE": {
	 *        "hand": [
	 *        	...
	 *        ],
	 *        "pair": "EW"
	 *      },
	 *      "playerW": {
	 *        "hand": [
	 *					...
	 *        ],
	 *        "pair": "EW"
	 *      },
	 *      "currentPlayer": "S",
	 *      "tricks": [
	 *        {
	 *          "cards": [
	 *
	 *            { "name": "2C", player: "W"}
	 *            { "name": "3C", player: "N"}
	 *            { "name": "4C", player: "E"}
	 *            { "name": "5C" player: "S"}
	 *          ],
	 *          "winningCard": "5C",
	 *          "winningPair": "NS"
	 *          "winningPlayer": "S"
	 *        }
	 *      ],
	 *      "score": {
	 *        "NS": 1,
	 *        "EW": 0
	 *      },
	 *      "scoreBoard": {
	 *        "contract": "3S XX",
	 *        "declarer": "N",
	 *        "tricks": 9,
	 *        "winner": "NS",
	 *        "points": 380
	 *      },
	 *      "isComplete": false
	 *    };
	 * @return {Object} the game at the present state
	 */
	get currentGameData() {
		let gameData = {
			name: this.name,
			boardNumber: this.board,
			deck: this.deck.cards.map((card) => card.name),
			dealer: this.dealer.name,
			dealString: this.dealString,
			auction: {
				bids: this.auctionInstance.asDetailedList,
				currentBidder: this.auctionInstance.currentBidder,
				isComplete: this.auctionInstance.isComplete,
				isDoubled: this.auctionInstance.isDoubled,
				isReDoubled: this.auctionInstance.isReDoubled,
				declarer: this.auctionInstance.declarer
					? this.auctionInstance.declarer.name
					: null,
			},
			vulnerable: this.vulnerable,
			declarer: this.declarer ? this.declarer.name : null,
			dummy: this.dummy ? this.dummy.name : null,
			contract: this.contract,
			contractSuit: this.contractSuit(),
			actions: this.cardsPlayed.map((card) => card.name),
			players: this.players.map((player) => player.name),
			playerN: {
				hand: this.playerN.handAsList,
				pair: this.playerN.pair,
				isVulnerable: this.playerN.isVulnerable(this.vulnerable),
			},
			playerS: {
				hand: this.playerS.handAsList,
				pair: this.playerS.pair,
				isVulnerable: this.playerS.isVulnerable(this.vulnerable),
			},
			playerE: {
				hand: this.playerE.handAsList,
				pair: this.playerE.pair,
				isVulnerable: this.playerE.isVulnerable(this.vulnerable),
			},
			playerW: {
				hand: this.playerW.handAsList,
				pair: this.playerW.pair,
				isVulnerable: this.playerW.isVulnerable(this.vulnerable),
			},
			currentPlayer: this.currentPlayer.name,
			tricks: this.tricks.map((trick) => {
				return {
					cards: trick.cards.map((card) => {
						return { name: card.name, player: card.player.name };
					}),
					winningCard: trick.winningCard.name,
					winningPair: trick.winningPair,
					winningPlayer: trick.winningCard.player.name,
				};
			}),
			score: this.score,
			scoreBoard: new ScoreBoard(this).generateEntry(),
			isComplete: this.isComplete,
			bidOptions: this.auctionInstance.serializeValidBidList,
			useDDS: this.useDDS,
		};

		return Object.assign({}, gameData); // make a copy
	}

	/**
	 * Removes the current DDS card scores from all cards, ready for the next calculation - used internally
	 */
	resetCardScores() {
		this.deck.cards.map((c) => (c.dds = undefined));
	}

	/**
	 * Calculates, and sets the card DDS scores for the current state of play
	 * @return {Object} the raw data from the DDS library
	 */
	nextPlays() {
		this.resetCardScores();

		if (!this._useDDS) {
			return;
		}
		// if there are no cards, the lib cannot calculate DDS scores, so return
		if (this.playableCards.length == 0) {
			return;
		}

		// do some crazy dds stuff
		const pbn = this.DDSDealString;

		const contractSuit = this.contractSuit();

		const trump = contractSuit == 'NT' ? 'N' : contractSuit;

		const cards = this.cardsPlayed.map((card) => card.name);

		const plays = DDS.nextPlays(pbn, trump, cards);

		if (!plays || plays.error) {
			throw new Error(`(${plays.error}) ${plays.message}`);
		}

		const isDeclaringPair = this.currentPlayer.pair == this.declaringPair;
		const pairScore = this.score[this.currentPlayer.pair];

		for (let ddsCard of plays.plays) {
			let card = this.deck.card(`${ddsCard.rank}${ddsCard.suit}`);

			let relativeTricks;

			if (isDeclaringPair) {
				// the declaring pair needs at least `minTricks` to win
				relativeTricks = ddsCard.score - this.minTricks + pairScore;
			} else {
				// the defending pair needs at least `13 - minTricks` to win
				relativeTricks = 13 - ddsCard.score - this.minTricks - pairScore;
			}

			card.dds = { score: ddsCard.score, tricks: relativeTricks };

			// also apply to all "equal" cards
			ddsCard.equals.forEach((rank) => {
				const eCard = this.deck.card(`${rank}${ddsCard.suit}`);
				eCard.dds = { score: ddsCard.score, tricks: relativeTricks };
			});
		}
		return plays;
	}

	/**
	 * Uses DDS to calculate the next card
	 * @param {string[]} prioritizeLowestCardsForSuits - a list of suits for which the DDS should prefer playing the lowest cards (by rank)
	 * @param {boolean} ddsDiscardsLowestCards - whether the DDS should discard the lowest possible cards first, if it cannot follow the suit
	 * @param {string[]} prioritizeSuits - the DDS will try to prioritize these suits when playing the card (1st - highest prio, last - lowest).
	 * @return {Card} standalone card object
	 */
	getNextCardFromDDS(
		prioritizeLowestCardsForSuits = [],
		ddsDiscardsLowestCards = false,
		prioritizeSuits = []
	) {
		const contractSuit = this.contractSuit();
		const trump = contractSuit === 'NT' ? 'N' : contractSuit;
		const cards = this.cardsPlayed.map((card) => card.name);

		const result = DDS.nextPlays(this.DDSDealString, trump, cards);

		console.log('DDS nextPlays result:');
		console.dir(result, { depth: null });

		if (!result || result.error) {
			throw new Error(`(${result.error}) ${result.message}`);
		}

		if (prioritizeSuits && prioritizeSuits.length) {
			const maxScoreSuits = getMaxScorePlays(result.plays).map(
				(play) => play.suit
			);

			const playsBySuits = getPlaysBySuits(
				result.plays,
				intersection(prioritizeSuits, maxScoreSuits)
			);

			if (playsBySuits.length) {
				result.plays = playsBySuits;
			}
		}

		const equalScores = isSamePropValue(result.plays, 'score');
		const shouldPrioritizeLowestCard =
			!!prioritizeLowestCardsForSuits.length &&
			isSamePropValue(result.plays, 'suit') &&
			prioritizeLowestCardsForSuits.includes(result.plays[0].suit);

		if (equalScores && shouldPrioritizeLowestCard) {
			const ranks = result.plays.map((play) => play.rank);
			const lowestRank = getLowestRank(ranks);
			const lowestRankPlay = result.plays.find(
				(play) => play.rank === lowestRank
			);

			const rank = lowestRankPlay.equals.length
				? getLowestRank(lowestRankPlay.equals)
				: lowestRankPlay.rank;

			return new Card(rank, lowestRankPlay.suit);
		}

		const maxScorePlays = getMaxScorePlays(result.plays);

		if (ddsDiscardsLowestCards) {
			const card = getLowestPlayCard(maxScorePlays);

			if (card) {
				return card;
			}
		}

		let maxScoreCard = maxScorePlays[0];
		let rank = maxScoreCard.rank;

		if (maxScoreCard.equals.length > 0) {
			rank = maxScoreCard.equals[maxScoreCard.equals.length - 1];
		}

		return new Card(rank, maxScoreCard.suit);
	}

	/**
	 * Uses DDS to calculate the next card and sets it in the current trick
	 * @param {string[]} prioritizeLowestCardsForSuits - a list of suits for which the DDS should prefer playing the lowest cards (by rank)
	 * @param {boolean} ddsDiscardsLowestCards - whether the DDS should discard the lowest possible cards first, if it cannot follow the suit
	 * @param {string[]} prioritizeSuits - the DDS will try to prioritize these suits when playing the card (1st - highest prio, last - lowest).
	 * @return {Card} card object from the current deck
	 */
	nextPlay(
		prioritizeLowestCardsForSuits = [],
		ddsDiscardsLowestCards = false,
		prioritizeSuits = []
	) {
		console.log('Playing card with DDS');

		console.log('DDS Tweaks: ');
		console.log('prioritizeLowestCardsForSuits', prioritizeLowestCardsForSuits);
		console.log('ddsDiscardsLowestCards', ddsDiscardsLowestCards);
		console.log('prioritizeSuits', prioritizeSuits);

		if (!this.canPlay()) {
			throw new Error('Not ready to play');
		}

		if (this.isComplete) {
			throw new Error('Game is complete');
		}

		const ddsCard = this.getNextCardFromDDS(
			prioritizeLowestCardsForSuits,
			ddsDiscardsLowestCards,
			prioritizeSuits
		);

		console.log('Chosen card: ');
		console.dir(ddsCard, { depth: null });

		const card = this.deck.card(ddsCard.name);

		this.playCard(card);

		return card;
	}

	/**
	 * Returns a GameState.state object, not the instance of GameState.
	 * @return {Object} a game state object suitable for serialization
	 */
	get state() {
		// could get crazy
		return new GameState(this).state;
	}

	/**
	 * Returns a GameState instance for this game.
	 * @return {Object} a game state instance
	 */
	get gameState() {
		// could get crazy
		return new GameState(this);
	}

	swapSubstring(input, start1, end1, start2, end2) {
		const firstPart =
			input.substring(0, start1) +
			input.substring(start2, end2) +
			input.substring(end1, start2);
		const secondPart = input.substring(start1, end1) + input.substring(end2);
		return firstPart + secondPart;
	}

	toPBNReversed(position) {
		// We need to handle if a card is played already
		if (position === 2 || position === 4) {
			return this.swapSubstring(this.DDSDealString, 2, 19, 36, 53);
		} else {
			const resultString = this.swapSubstring(
				this.DDSDealString + ' ',
				19,
				36,
				53,
				70
			);
			return resultString.trim();
		}
	}
}

/**
 * The order of play follwing for the given winning seat indexed by seat name `N`, `E`, `S`, `W`.
 * The winner of the last trick goes first.
 * Used internally to determine `this.seatOrder`
 *  e.g.
 *   <pre>
 *    <code>
 *    	{
 *    		N: ["N", "E", "S", "W"],
 *    		E: ...
 *    	}
 *   </pre>
 *  </code>
 * @type {Object}
 * @private
 */
Game.PLAYORDER = PLAYORDER;

/**
 * The order of play for the given dealer indexed by seat name `N`, `E`, `S`, `W`.
 * The player to the left of dealer goes first
 * Used internally to determine `this.seatOrder`
 *  e.g.
 *   <pre>
 *    <code>
 *    	{
 *    		N: ["E", "S", "W", "N"],
 *    		E: ...
 *    	}
 *   </pre>
 *  </code>
 * @type {Object}
 * @private
 */

Game.NEXT_PLAYER_ORDER = NEXT_PLAYER_ORDER;

module.exports = Game;

/**
 * Create a new Game instance with deck, players and dealer
 *
 * This acts as a factory to create a new game without the need to set it up directly
 *
 * @param  {String} name the name of the game to create e.g. "Weak 2s"
 * @return {Game} an instance of a game, ready to start
 * @example
 * let game = Game.NewGame("A new game");
 * game.name; // "A New game"
 */
module.exports.NewGame = (name, boardNumber = undefined, useDDS = false) => {
	let newGame = new Game(name, boardNumber, useDDS);
	newGame.newGame();
	return newGame;
};

module.exports.fromState = (stateObject) => {
	return DealParser.fromState(stateObject);
};

module.exports.fromCurrentGameData = (gameDataObject, useDDS = false) => {
	return DealParser.fromCurrentGameData(gameDataObject, useDDS);
};

module.exports.fromDealString = (dealString, metadata = {}, useDDS = false) => {
	return DealParser.fromDealString(dealString, metadata, useDDS);
};
